import axios from 'axios';

// Use relative path so it works with Vite proxy (dev) and same-origin (cloudflared)
// Vite proxy will forward /api to localhost:3001 in dev mode
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Absolute URL for a file the backend serves (student photos live under
 * /uploads). Normally the app and the API share an origin and the stored path
 * is already correct — but when VITE_API_URL points at a different host, a bare
 * "/uploads/..." would resolve against the frontend and silently render as a
 * broken image.
 */
export const assetUrl = (path) => {
  if (!path) return '';
  // Already absolute, or a freshly-picked image still held in the browser.
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path;
  const base = import.meta.env.VITE_API_URL;
  if (!base || base.startsWith('/')) return path;
  return `${base.replace(/\/api\/?$/, '').replace(/\/$/, '')}${path}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A dead token (expired, or the server's secret changed) fails every call,
    // leaving the app half-broken — zeroed stats, empty pickers — until the
    // admin thinks to log out. Send them back to the login page instead.
    // Login's own 401 (wrong password) must not trigger a reload.
    const status = error.response?.status;
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    const isDeadToken =
      status === 401 ||
      (status === 403 && error.response?.data?.error === 'Invalid or expired token');
    if (isDeadToken && !isLoginRequest && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (credentials) => {
  return api.post('/auth/login', credentials);
};

// Classes
export const getClasses = async () => {
  return api.get('/classes');
};

// Public Classes (sanitized - no student names)
export const getPublicClasses = async () => {
  return api.get('/classes/public');
};

export const createClass = async (classData) => {
  return api.post('/classes', classData);
};

export const updateClass = async (id, classData) => {
  return api.put(`/classes/${id}`, classData);
};

export const deleteClass = async (id) => {
  return api.delete(`/classes/${id}`);
};

// Students
export const addStudentToClass = async (classId, studentData) => {
  return api.post(`/classes/${classId}/students`, studentData);
};

export const updateStudent = async (id, studentData) => {
  return api.put(`/students/${id}`, studentData);
};

export const removeStudent = async (id) => {
  return api.delete(`/students/${id}`);
};

export const moveStudent = async (studentId, classId, targetClassId) => {
  return api.post(`/students/${studentId}/move`, { classId, targetClassId });
};

// Instructors
export const getInstructors = async (classType = null) => {
  const params = classType ? { classType, active: 'true' } : { active: 'true' };
  return api.get('/instructors', { params });
};

export const createInstructor = async (instructorData) => {
  return api.post('/instructors', instructorData);
};

export const updateInstructor = async (id, instructorData) => {
  return api.put(`/instructors/${id}`, instructorData);
};

export const deleteInstructor = async (id) => {
  return api.delete(`/instructors/${id}`);
};

// Locations
export const getLocations = async () => {
  const params = { active: 'true' };
  return api.get('/locations', { params });
};

export const createLocation = async (locationData) => {
  return api.post('/locations', locationData);
};

export const updateLocation = async (id, locationData) => {
  return api.put(`/locations/${id}`, locationData);
};

export const deleteLocation = async (id) => {
  return api.delete(`/locations/${id}`);
};

// Student Profiles
export const getStudentProfiles = async () => {
  return api.get('/student-profiles');
};

export const getStudentDirectory = async (params = {}) => {
  return api.get('/student-profiles/directory', { params });
};

export const getDirectoryFilters = async () => {
  return api.get('/student-profiles/directory/filters');
};

// Existing students who might be the child about to be created.
export const findSimilarStudents = async (name) => {
  return api.get('/student-profiles/similar', { params: { name } });
};

export const getStudentProfile = async (id) => {
  return api.get(`/student-profiles/${id}`);
};

export const updateStudentProfile = async (id, data) => {
  return api.put(`/student-profiles/${id}`, data);
};

// Adds a student to a class. Passing existingProfileId links a child who is
// already in the system into a second class instead of creating a duplicate.
export const createStudent = async (data) => {
  return api.post('/student-profiles/students', data);
};

// Parent email import (Stripe export)
export const previewEmailImport = async (text) => {
  return api.post('/email-import/preview', { text });
};

export const applyEmailImport = async (assignments) => {
  return api.post('/email-import/apply', { assignments });
};

// Parent invites
export const sendParentInvite = async (studentProfileId, email) => {
  return api.post('/parent-invites', { studentProfileId, email });
};

export const sendBulkParentInvites = async (studentProfileIds) => {
  return api.post('/parent-invites/bulk', { studentProfileIds });
};

export const getInviteStatus = async () => {
  return api.get('/parent-invites/status');
};

// Reports
export const getReportInstructors = async () => {
  return api.get('/reports/instructors');
};

export const getInstructorPerformance = async (name) => {
  return api.get(`/reports/instructor/${encodeURIComponent(name)}`);
};

// Instagram Posts
export const getInstagramPosts = async () => {
  return api.get('/instagram-posts');
};

export const createInstagramPost = async (postData) => {
  return api.post('/instagram-posts', postData);
};

export const updateInstagramPost = async (id, postData) => {
  return api.put(`/instagram-posts/${id}`, postData);
};

export const deleteInstagramPost = async (id) => {
  return api.delete(`/instagram-posts/${id}`);
};

// Attendance
export const getAttendanceDay = async (date) => {
  return api.get(`/attendance/day/${date}`);
};

export const getAttendanceSession = async (classId, date) => {
  return api.get(`/attendance/session/${classId}/${date}`);
};

export const saveAttendanceSession = async (classId, date, entries) => {
  return api.put(`/attendance/session/${classId}/${date}`, { entries });
};

export const deleteAttendanceSession = async (classId, date) => {
  return api.delete(`/attendance/session/${classId}/${date}`);
};

export const getAttendanceMonth = async (yyyymm) => {
  return api.get(`/attendance/month/${yyyymm}`);
};

export const getAttendanceOverview = async () => {
  return api.get('/attendance/report/overview');
};

export const getAttendanceClassReport = async (classId) => {
  return api.get(`/attendance/report/class/${classId}`);
};

export const getAttendanceStudentReport = async (studentKey) => {
  return api.get(`/attendance/report/student/${studentKey}`);
};

// Ceremonies
export const getCeremonies = async () => api.get('/ceremonies');
export const createCeremony = async (data) => api.post('/ceremonies', data);
export const getCeremony = async (id) => api.get(`/ceremonies/${id}`);
export const updateCeremony = async (id, data) => api.patch(`/ceremonies/${id}`, data);
export const getCeremonyCandidates = async (id, params = {}) =>
  api.get(`/ceremonies/${id}/candidates`, { params });
export const addCeremonyStudents = async (id, studentKeys) =>
  api.post(`/ceremonies/${id}/students`, { studentKeys });
export const removeCeremonyStudent = async (id, studentKey) =>
  api.delete(`/ceremonies/${id}/students/${studentKey}`);
// Saves one student's row. Called as the admin types, so it is deliberately
// small and safe to retry.
export const saveCeremonyResult = async (id, studentKey, changes) =>
  api.put(`/ceremonies/${id}/results/${studentKey}`, changes);
export const generateCertificates = async (id, options = {}) =>
  api.post(`/ceremonies/${id}/certificates`, options);
export const publishCeremony = async (id, options = {}) =>
  api.post(`/ceremonies/${id}/publish`, options);

// Competition teams
export const getCompetitionTeams = async () => api.get('/competition/teams');
export const createCompetitionTeam = async (data) => api.post('/competition/teams', data);
export const getCompetitionTeam = async (id, params = {}) =>
  api.get(`/competition/teams/${id}`, { params });
export const updateCompetitionTeam = async (id, data) => api.patch(`/competition/teams/${id}`, data);
export const deleteCompetitionTeam = async (id) => api.delete(`/competition/teams/${id}`);
export const getCompetitionCandidates = async (id, params = {}) =>
  api.get(`/competition/teams/${id}/candidates`, { params });
export const addCompetitionMembers = async (id, studentKeys) =>
  api.post(`/competition/teams/${id}/members`, { studentKeys });
export const updateCompetitionMember = async (id, studentKey, data) =>
  api.patch(`/competition/teams/${id}/members/${studentKey}`, data);
export const removeCompetitionMember = async (id, studentKey) =>
  api.delete(`/competition/teams/${id}/members/${studentKey}`);
export const getCompetitionSession = async (id, params = {}) =>
  api.get(`/competition/teams/${id}/session`, { params });
// Saves one dancer's row for one session. Called as the coach types.
export const saveCompetitionStat = async (id, studentKey, data) =>
  api.put(`/competition/teams/${id}/stats/${studentKey}`, data);
export const deleteCompetitionSession = async (id, params = {}) =>
  api.delete(`/competition/teams/${id}/session`, { params });
export const getCompetitionDancer = async (id, studentKey) =>
  api.get(`/competition/teams/${id}/students/${studentKey}`);

// ── Instructor iPad ─────────────────────────────────────────────────────────────
// A separate client, because the wall-mounted iPad authenticates with a device
// token rather than a login. It deliberately does NOT share the interceptor
// that bounces an expired session to the login page — there is no login page
// here, and an instructor mid-class must never be shown one.

const DEVICE_TOKEN_KEY = 'l2b-device-token';

export const getDeviceToken = () => localStorage.getItem(DEVICE_TOKEN_KEY) || '';
export const setDeviceToken = (token) => localStorage.setItem(DEVICE_TOKEN_KEY, token);
export const clearDeviceToken = () => localStorage.removeItem(DEVICE_TOKEN_KEY);

const deviceApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

deviceApi.interceptors.request.use((config) => {
  // Left off entirely rather than sent empty when there is nothing stored, so
  // the server falls through to the device cookie it set at pairing. That is
  // what keeps an iPad signed in after iOS clears its localStorage, or when the
  // home-screen app opens with a storage container of its own.
  const token = getDeviceToken();
  if (token) config.headers['x-device-token'] = token;
  else delete config.headers['x-device-token'];
  return config;
});

// Exchanges the six-character pairing code for this device's own token.
export const pairDevice = async (code) => deviceApi.post('/instructor/pair', { code });
export const getInstructorSession = async () => deviceApi.get('/instructor/session');
export const getPortalInstructors = async () => deviceApi.get('/instructor/instructors');
export const getInstructorSchedule = async (name) =>
  deviceApi.get(`/instructor/schedule/${encodeURIComponent(name)}`);
export const getRegister = async (classId, date) =>
  deviceApi.get(`/instructor/class/${classId}`, { params: date ? { date } : {} });
export const saveRegister = async (classId, { date, entries, instructor }) =>
  deviceApi.put(`/instructor/class/${classId}/attendance`, { date, entries, instructor });
export const getClassOverview = async (classId) =>
  deviceApi.get(`/instructor/class/${classId}/overview`);
export const setCeremonyRsvp = async (studentKey, rsvp, instructor) =>
  deviceApi.post('/instructor/ceremony-rsvp', { studentKey, rsvp, instructor });

// Admin Users
export const getAdminUsers = async () => {
  return api.get('/admin/users');
};

export const createAdminUser = async (userData) => {
  return api.post('/admin/users', userData);
};

export const deleteAdminUser = async (userId) => {
  return api.delete(`/admin/users/${userId}`);
};

export const resetAdminPassword = async (userId, newPassword, confirmPassword) => {
  return api.put(`/admin/users/${userId}/reset-password`, {
    newPassword,
    confirmPassword,
  });
};

// Snapshots (for future use)
export const getSnapshots = async () => {
  return api.get('/snapshots');
};

export const createSnapshot = async (snapshotData) => {
  return api.post('/snapshots', snapshotData);
};

export default api;

